import { useState } from 'react';
import type { AdminAgent } from '@sd/types/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Search, UserPlus, Edit2, Ticket, TrendingUp, Star, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';

const roleLabels: Record<string, string> = {
  agent: 'Agent',
  senior_agent: 'Senior Agent',
  supervisor: 'Supervisor',
};

const roleColors: Record<string, string> = {
  agent: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  senior_agent: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  supervisor: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
};

const availabilityColors: Record<string, string> = {
  online: 'text-green-500',
  busy: 'text-yellow-500',
  offline: 'text-gray-400',
};

export function AdminUserManagement() {
  const [agents, setAgents] = useState<AdminAgent[]>([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [editDialog, setEditDialog] = useState<{ open: boolean; agent: AdminAgent | null }>({ open: false, agent: null });
  const [addDialog, setAddDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '', department: '' });
  const [addForm, setAddForm] = useState({ name: '', email: '', role: 'agent', department: 'IT Support' });

  const filteredAgents = agents.filter(a => {
    const matchesSearch = search === '' ||
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      a.email.toLowerCase().includes(search.toLowerCase());
    const matchesRole = roleFilter === 'all' || a.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const activeAgents = agents.filter(a => a.status === 'active').length;
  const onlineAgents = agents.filter(a => a.availability === 'online').length;
  const totalAssigned = agents.reduce((sum, a) => sum + a.assignedTickets, 0);
  const avgCompliance = Math.round(agents.filter(a => a.status === 'active').reduce((sum, a) => sum + a.slaCompliance, 0) / (activeAgents || 1));
  const avgCsat = (agents.filter(a => a.status === 'active').reduce((sum, a) => sum + a.csatAvg, 0) / (activeAgents || 1)).toFixed(1);

  const openEditDialog = (agent: AdminAgent) => {
    setEditForm({ name: agent.name, email: agent.email, role: agent.role, department: agent.department });
    setEditDialog({ open: true, agent });
  };

  const handleSaveEdit = () => {
    if (!editDialog.agent) return;
    setAgents(prev => prev.map(a =>
      a.id === editDialog.agent!.id
        ? { ...a, name: editForm.name, email: editForm.email, role: editForm.role as AdminAgent['role'], department: editForm.department }
        : a
    ));
    toast.success(`User ${editForm.name} updated`);
    setEditDialog({ open: false, agent: null });
  };

  const handleAddAgent = () => {
    const newAgent: AdminAgent = {
      id: `ag-${Date.now()}`,
      name: addForm.name,
      email: addForm.email,
      role: addForm.role as AdminAgent['role'],
      department: addForm.department,
      status: 'active',
      availability: 'offline',
      assignedTickets: 0,
      resolvedToday: 0,
      avgResolutionTime: '0h 0m',
      slaCompliance: 100,
      csatAvg: 0,
      joinedAt: new Date().toISOString().split('T')[0],
      skills: [],
      maxCapacity: 10,
    };
    setAgents(prev => [...prev, newAgent]);
    toast.success(`Agent ${addForm.name} added`);
    setAddDialog(false);
    setAddForm({ name: '', email: '', role: 'agent', department: 'IT Support' });
  };

  const toggleAgentStatus = (agentId: string) => {
    setAgents(prev => prev.map(a =>
      a.id === agentId ? { ...a, status: a.status === 'active' ? 'inactive' : 'active', availability: a.status === 'active' ? 'offline' : 'online' } : a
    ));
    const agent = agents.find(a => a.id === agentId);
    toast.success(`${agent?.name} is now ${agent?.status === 'active' ? 'inactive' : 'active'}`);
  };

  const cycleAvailability = (agentId: string) => {
    const cycle: Record<string, 'online' | 'busy' | 'offline'> = { online: 'busy', busy: 'offline', offline: 'online' };
    setAgents(prev => prev.map(a =>
      a.id === agentId ? { ...a, availability: cycle[a.availability] } : a
    ));
  };

  const workloadData = agents.filter(a => a.status === 'active').map(a => ({
    name: a.name.split(' ')[0],
    assigned: a.assignedTickets,
    capacity: a.maxCapacity,
    utilization: Math.round((a.assignedTickets / a.maxCapacity) * 100),
  }));

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Users & Agents</h1>
          <p className="text-muted-foreground">Manage team members, roles, capacity and performance</p>
        </div>
        <Button onClick={() => setAddDialog(true)}>
          <UserPlus className="h-4 w-4 mr-2" />Add Agent
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Agents</p>
            <p className="text-2xl font-bold">{agents.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Active</p>
            <p className="text-2xl font-bold text-green-600">{activeAgents}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Online Now</p>
            <p className="text-2xl font-bold text-blue-600">{onlineAgents}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Avg SLA</p>
            <p className="text-2xl font-bold">{avgCompliance}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Avg CSAT</p>
            <p className="text-2xl font-bold flex items-center gap-1"><Star className="h-4 w-4 text-yellow-500" />{avgCsat}</p>
          </CardContent>
        </Card>
      </div>

      {/* Workload Chart */}
      <Card>
        <CardHeader><CardTitle className="text-base">Agent Workload & Capacity</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={workloadData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="name" className="text-xs" />
              <YAxis className="text-xs" />
              <Tooltip />
              <Legend />
              <Bar dataKey="assigned" name="Assigned" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
              <Bar dataKey="capacity" name="Max Capacity" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} opacity={0.3} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search agents..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="agent">Agent</SelectItem>
            <SelectItem value="senior_agent">Senior Agent</SelectItem>
            <SelectItem value="supervisor">Supervisor</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Agents Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Agent</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Availability</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">Workload</TableHead>
              <TableHead className="text-center">Resolved</TableHead>
              <TableHead>Resolution Time</TableHead>
              <TableHead>SLA %</TableHead>
              <TableHead>CSAT</TableHead>
              <TableHead>Skills</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAgents.map((agent) => (
              <TableRow key={agent.id}>
                <TableCell>
                  <div>
                    <div className="font-medium">{agent.name}</div>
                    <div className="text-xs text-muted-foreground">{agent.email}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={cn("text-xs", roleColors[agent.role])}>{roleLabels[agent.role]}</Badge>
                </TableCell>
                <TableCell>
                  <button onClick={() => cycleAvailability(agent.id)} className="flex items-center gap-1.5 cursor-pointer">
                    <Circle className={cn("h-2.5 w-2.5 fill-current", availabilityColors[agent.availability])} />
                    <span className="text-xs capitalize">{agent.availability}</span>
                  </button>
                </TableCell>
                <TableCell>
                  <Switch checked={agent.status === 'active'} onCheckedChange={() => toggleAgentStatus(agent.id)} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Progress value={(agent.assignedTickets / agent.maxCapacity) * 100} className="h-2 w-16" />
                    <span className="text-xs font-medium">{agent.assignedTickets}/{agent.maxCapacity}</span>
                  </div>
                </TableCell>
                <TableCell className="text-center font-medium">{agent.resolvedToday}</TableCell>
                <TableCell className="text-sm">{agent.avgResolutionTime}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <TrendingUp className={cn("h-3 w-3", agent.slaCompliance >= 90 ? 'text-green-500' : agent.slaCompliance >= 80 ? 'text-yellow-500' : 'text-red-500')} />
                    <span className="text-sm font-medium">{agent.slaCompliance}%</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Star className={cn("h-3 w-3", agent.csatAvg >= 4.5 ? 'text-yellow-500' : 'text-muted-foreground')} />
                    <span className="text-sm font-medium">{agent.csatAvg}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-0.5">
                    {agent.skills.slice(0, 2).map(s => (
                      <Badge key={s} variant="outline" className="text-[10px] px-1 py-0">{s}</Badge>
                    ))}
                    {agent.skills.length > 2 && <span className="text-[10px] text-muted-foreground">+{agent.skills.length - 2}</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(agent)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, agent: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Agent</DialogTitle>
            <DialogDescription>Update agent details and role</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={editForm.name} onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={editForm.email} onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))} /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={(val) => setEditForm(prev => ({ ...prev, role: val }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="senior_agent">Senior Agent</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Department</Label><Input value={editForm.department} onChange={(e) => setEditForm(prev => ({ ...prev, department: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, agent: null })}>Cancel</Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Agent Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Agent</DialogTitle>
            <DialogDescription>Add a new team member to the helpdesk</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Full Name</Label><Input value={addForm.name} onChange={(e) => setAddForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Enter full name" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={addForm.email} onChange={(e) => setAddForm(prev => ({ ...prev, email: e.target.value }))} placeholder="agent@company.com" /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={addForm.role} onValueChange={(val) => setAddForm(prev => ({ ...prev, role: val }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="senior_agent">Senior Agent</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Department</Label><Input value={addForm.department} onChange={(e) => setAddForm(prev => ({ ...prev, department: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddAgent} disabled={!addForm.name || !addForm.email}>Add Agent</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
